"use client"
import React, { useEffect, useRef, useState } from "react"
import { useTransitionRouter } from "next-view-transitions"
import sanitizeHtml from 'sanitize-html'
import Image from "next/image"
import Flip from "gsap/Flip"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import ReactLenis from "lenis/react"
import { useRouter } from "next/navigation" 
import { IconMouse, IconQuote } from "@tabler/icons-react"
import { useGSAP } from "@gsap/react"
import { slideInOut } from "@/app/lib/gsapAnimation"
import ImagePlaceholder from "../ui/ImagePlaceHolder"
import Testimonial from "../ui/Testimonial"
import TextSplit from "../ui/TextSplit"
import { InteractiveHoverButton } from "../magicui/interactive-hover-button"

gsap.registerPlugin(Flip)

export default function Post({ data, previous, type = "insights" }) {

  const { slug, title, acf, content, excerpt, featuredImageSrc, featuredImageAlt, next, categories } = data
  const router = useRouter()
  const transitionRouter = useTransitionRouter()
  const articleRef = useRef<HTMLImageElement>(null)
  const [isloading, setIsloading] = useState(false)
  console.log(setIsloading)

  // header progress
  const projectNavRef = useRef(null)
  const progressBarRef = useRef(null)
  const footerScrollTriggerRef = useRef<ScrollTrigger | null>(null)

  // Conent
  const projectDescriptionRef = useRef(null)

  // Footer progress
  const footerRef = useRef(null)
  const nextProjectProgressBarRef = useRef(null)

  // Track is a page transition is in progress
  const [isTransitionning, setIsTransitionning] = useState(false)

  // Footer bar continue updating once we scroll and disable while page transition happens
  const [shouldUpdateProgress, setShouldUpdateProgress] = useState(true)

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger)

    // on cale le nav en haut
    gsap.set(projectNavRef.current, {
      opacity: 0,
      y: -100
    })

    gsap.to(projectNavRef.current, {
      opacity: 1,
      y: 0,
      duration: 1,
      delay: 0.5,
      ease: "power3.out"
    })

    gsap.to(projectDescriptionRef.current, {
      opacity: 1,
      y: 0, // not sure
      duration: 1,
      delay: 0.5,
      ease: "power3.out"
    })

    // not sure
    gsap.to(footerRef.current, {
      opacity: 1,
      duration: 1,
      delay: 0.5,
      ease: "power3.out"
    })

    /*     const navScrollTrigger = ScrollTrigger.create({
      trigger: document.body,
      start: "top top",
      end: "bottom bottom",
      onUpdate: self => {
        if (progressBarRef.current) {
          gsap.set(progressBarRef.current, {
            scaleX: self.progress
          })
        }
      }
    }) */

    // let footerScrollTrigger

    footerScrollTriggerRef.current = ScrollTrigger.create({
      trigger: footerRef.current,
      start: "top bottom",
      end: "bottom bottom",
      scrub: 3,
      onEnter: () => {
        if (projectNavRef.current && !isTransitionning) {
          gsap.to(projectNavRef.current, {
            y: -100,
            duration: 0.5,
            ease: "power2.inOut"
          })
        }
      },
      onLeaveBack: () => {
        if (projectNavRef.current && !isTransitionning) {
          gsap.to(projectNavRef.current, {
            y: 0,
            duration: 0.5,
            ease: "power2.inOut"
          })
        }
      },
      onUpdate: self => {
        const easedProgress = self.progress < 0.99 ? self.progress : Math.min(1, gsap.utils.mapRange(0.99, 1, 0.99, 1, self.progress)) // guarantee if goes to 1 and Slow down near 1

        // Timeline
        const tl = gsap.timeline()

        if (easedProgress >= 0.1) {
          // If the progress is close to 1, increase the duration to slow down the transition
          tl.to(nextProjectProgressBarRef.current, {
            scaleX: easedProgress,
            delay: 0.5,
            duration: 2, // Longer duration for slow down
            ease: "power2.inOut"
          })
        } else {
          tl.to(nextProjectProgressBarRef.current, {
            scaleX: easedProgress,
            duration: 1, // Normal transition speed
            ease: "power2.inOut"
          })
        }

        if (nextProjectProgressBarRef.current && shouldUpdateProgress) {
          gsap.set(nextProjectProgressBarRef.current, {
            scaleX: easedProgress,
            duration: easedProgress >= 0.9 ? 2 : 1,
            ease: "power2.inOut"
          })
        }

        if (self.progress >= 0.99 && !isTransitionning) {
          setShouldUpdateProgress(false)
          setIsTransitionning(true)

          footerScrollTriggerRef.current?.disable()

          // tl.play()

          if (nextProjectProgressBarRef.current) {
            tl.set(nextProjectProgressBarRef.current, {
              scaleX: 1
            })
          }

          const footerCopy = footerRef.current?.querySelector(".project-footer-copy")
          const nextProgress = footerRef.current?.querySelector(".next-project-progress")

          if (footerCopy || nextProgress) {
            tl.to([footerCopy, nextProgress].filter(Boolean), {
              opacity: 0,
              duration: 0.3,
              ease: "power2.inOut"
            })
          }

          tl.call(() => {
            router.push(`/${type}/${next.slug}`)
          })
        }
      }
    })

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill())
    }
  }, [next.slug, isTransitionning, shouldUpdateProgress])

  useGSAP(
    () => {
      gsap.fromTo(articleRef.current, { opacity: 0, y: 40 }, { opacity: 1, delay: 0.25, duration: 0.5, y: 0 })
    },
    { scope: articleRef }
  )

  return (
    <ReactLenis root>
      <div className={`relative overflow-x-hidden ${type === "insights" ? "container" : ""}`}>
        <div ref={projectNavRef} className="h-3 sticky top-20 w-full mt-20">
          <div ref={progressBarRef} className="project-page-scroll-progress-bar absolute top-0 right-0 w-16 h-full bg-lime-700 scale-x-0 origin-left will-change-transform z-[1] border-black"></div>
        </div>
        <div className={`relative ${type === "work" ? "container" : ""}`}>
          <div className="mt-16 max-w-screen-2xl mx-auto px-0 lg:px-8 2xl:px-0 mb-8 2xl:mb-0 ">
            <div className="mb-5">
              <a
                onClick={e => {
                  e.preventDefault()
                  setTimeout(() => {
                    transitionRouter.push(`/${type}`, { onTransitionReady: slideInOut })
                  }, 300)
                }}
                title={`${type} page`}
              >
                <InteractiveHoverButton>List of {`${type}`} ←</InteractiveHoverButton>
              </a>
            </div>

            <article className="grid lg:grid-cols-2 lg:gap-8 pb-12 lg:pb-4 xl:mb-12">
              <div className="mt-8 lg:mt-0 flex flex-col lg:mx-8 gap-4 justify-center lg:max-w-[540px] pb-8" ref={articleRef}>
                <h1 dangerouslySetInnerHTML={{ __html: sanitizeHtml(title) }} className="text-3xl lg:text-5xl pb-0" style={{ viewTransitionName: "popup-text" }} />
                {type === "insights" && (
                  <div className="flex gap-x-4 gap-y-4 flex-wrap">
                    {categories.map((category, index) => (
                      <span key={index} className="text-base px-2 py-1 bg-slate-100">
                        #{category}
                      </span>
                    ))}
                  </div>
                )}
                {type === "work" && (
                  <TextSplit>
                    <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(excerpt, { ALLOWED_TAGS: [] }) }} />
                  </TextSplit>
                )}
              </div>
              <figure className="relative aspect-[4/3] lg:aspect-square 2xl:aspect-[4/3] w-full max-w-full">
                {/* Image peristante */}
                  <Image src={featuredImageSrc} alt={featuredImageAlt} className="opacity-1 object-cover rounded-2xl h-full w-full" width={1600} height={1200} style={{viewTransitionName: `${type}-${slug}`}}/>
              </figure>
            </article>
          </div>
        </div>

        {/* INSIGHTS */}

        {type === "insights" && (
          <div className="blog-post mb-72 pt-12">
            <div ref={projectDescriptionRef} id="project-description" className="prose text-xl max-w-[38rem] mx-auto leading-normal" dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }} />
          </div>
        )}

        {/* WORK */}
        {type === "work" && (
          <article>
            <div className="w-full h-full blur-3xl"></div>
            <div>
              {isloading && <div className="fixed bg-white w-screen h-screen z-[9999] opacity-50 transition-opacity duration-500 ease-in-out delay-75"></div>}

              {/* Challenge */}
              <section className="w-full linear-gradient-dark-light-green">
                <div className="container py-20 flex flex-col md:grid md:grid-cols-3 gap-x-8 items-center">
                  <div className="text-base lg:text-lg flex flex-col text-white pb-8">
                    <h3 className="font-semibold pb-8">What we did</h3>
                    <div dangerouslySetInnerHTML={{ __html: acf["work_provided_services"] }} className="text-base" />
                  </div>
                  <div className="md:col-span-2">

                    {acf?.work_challenge_video ? (
                      <video width="1280" height="960" autoPlay loop={false} muted preload="auto">
                        <source src={acf?.work_challenge_video?.url} type={acf?.work_challenge_video?.mime_type} />
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <ImagePlaceholder imageUrl={acf?.work_challenge_illustration?.url} alt={acf?.work_challenge_illustration?.alt} isVertical={true} />
                    )}
                  </div>
                </div>
              </section>

              {/* Solution */}
              {acf?.work_solution && (
                <section className="min-h-screen linear-yellow-orange text-white px-6 md:px-12 xl:px-16 items-center flex flex-col justify-center py-20 xl:py-32">
                  <div className="grid md:grid-cols-3 gap-12">
                    {/* Challenges */}
                    <div className="flex flex-col">
                      <h3 className="font-semibold md:pb-8">Challenges</h3>
                      <div dangerouslySetInnerHTML={{ __html: acf["work_challenge"] }} className="text-base" />
                    </div>

                    {/* Solution */}

                    <div className="md:col-span-2 flex flex-col">
                      <h3 className="font-semibold md:pb-8">Solution</h3>

                      <div className="grid grid-cols-[repeat(auto-fit,_minmax(300px,_1fr))] gap-x-8 text-base" dangerouslySetInnerHTML={{ __html: sanitizeHtml(acf?.work_solution) }} />
                    </div>
                  </div>
                </section>
              )}

              {/* Gallery */}
              {acf?.work_gallery?.map((panel, i) => {
                return (
                  <section key={i}>
                    <div className="container py-20 flex flex-col md:grid md:grid-cols-3 gap-x-8 items-center">
                      <div className="text-base lg:text-lg flex flex-col text-end">
                        <TextSplit animateOnScroll={true} delay={0.25}>
                          <div className="text-lg pb-8" dangerouslySetInnerHTML={{ __html: panel["work_gallery_media_legend"] }} />
                        </TextSplit>
                      </div>
                      <div className="md:col-span-2">
                        <ImagePlaceholder imageUrl={panel?.work_gallery_media?.url} alt={panel?.work_gallery_media?.alt} isVertical={true} />
                      </div>
                    </div>
                  </section>
                )
              })}

              {/* Testimonials */}
              {acf?.work_testimonials?.[0] && (
                <section className="panel bg-violet-100 xl:px-16 ">
                  <div className="flex gap-20 items-center justify-center w-full px-4">
                    <div className="flex flex-col max-w-screen-sm items-center justify-center">
                      <div>
                        <IconQuote className="size-10" />
                      </div>
                      <h3 className="font-semibold">Client testimonial</h3>
                      <Testimonial id={acf?.work_testimonials[0]} />
                    </div>
                  </div>
                </section>
              )}
            </div>
          </article>
        )}
      </div>

      {/* FOOTER */}

      <div className="py-6 md:py-12 lg:py-0 bg-[#003831] h-screen">
        <div ref={footerRef} className="container relative flex flex-col-reverse md:grid md:grid-cols-2 items-center justify-center md:mx-auto px-8 gap-8 h-full">
          <figure className="relative aspect-square 2xl:aspect-[4/3] w-full max-h-[600px]">
            {/* Image peristante */}


              <Image src={previous._embedded["wp:featuredmedia"][0].source_url} alt={previous._embedded["wp:featuredmedia"][0].alt_text} className="opacity-1 object-cover rounded-2xl h-full w-full" width={1600} height={1200} style={{viewTransitionName:`${type}-${previous.slug}`}}/>
          </figure>
          <div className="grid md:grid-cols-2 lg:flex lg:flex-col w-full">
            <div>
              <div className="my-6 text-white">
                Scroll to next insight <IconMouse className="inline" />
              </div>
              <h1 dangerouslySetInnerHTML={{ __html: sanitizeHtml(previous?.title?.rendered) }} className="text-3xl lg:text-5xl pb-0 text-[#FF3C5C]" />
            </div>
            <div className="relative">
              <div className="next-project-progress absolute bottom-full md:bottom-1/4 w-3/4 lg:w-1/4 h-1 bg-[#FF3C5C] top-3 md:top-8">
                <div ref={nextProjectProgressBarRef} className="next-project-progress-bar absolute top-0 left-0 w-full h-full scale-x-0 will-change-transform bg-white"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ReactLenis>
  )
}
